import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Link,
  useParams
} from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft,
  CheckCircle2,
  Clock3,
  Dumbbell,
  Pause,
  Play,
  RotateCcw,
  SkipForward,
  Trophy
} from 'lucide-react';

function formatarTempo(segundos) {
  const total = Math.max(
    0,
    Math.ceil(Number(segundos || 0))
  );

  const minutos = Math.floor(total / 60);
  const resto = total % 60;

  return `${String(minutos).padStart(2, '0')}:${String(resto).padStart(2, '0')}`;
}

export default function AlunoTreinoCasaExecutarPage() {
  const { treinoCasaId } = useParams();

  const [treino, setTreino] = useState(null);
  const [loading, setLoading] = useState(true);

  const [iniciado, setIniciado] = useState(false);
  const [finalizado, setFinalizado] = useState(false);

  const [indice, setIndice] = useState(0);
  const [rodada, setRodada] = useState(1);

  const [fase, setFase] = useState('exercicio');
  const [rodando, setRodando] = useState(false);
  const [restante, setRestante] = useState(0);

  const fimFaseRef = useRef(null);
  const restantePausadoRef = useRef(0);

  const exercicios = useMemo(() => {
    const lista = Array.isArray(treino?.exercicios)
      ? treino.exercicios
      : [];

    return [...lista].sort(
      (a, b) =>
        Number(a?.ordem || 0) -
        Number(b?.ordem || 0)
    );
  }, [treino?.exercicios]);

  const totalRodadas = Math.max(
    1,
    Number(treino?.rodadas || 1)
  );

  const exercicio = exercicios[indice];

  const totalEtapas = Math.max(
    1,
    exercicios.length * totalRodadas
  );

  const etapaAtual = Math.min(
    totalEtapas,
    ((rodada - 1) * exercicios.length)
      + indice
      + 1
  );

  const progresso = finalizado
    ? 100
    : Math.round(
        ((etapaAtual - 1) / totalEtapas) * 100
      );

  const definirContagem = useCallback(
    (segundos, iniciar = true) => {
      const valor = Math.max(
        0,
        Number(segundos || 0)
      );

      setRestante(valor);
      restantePausadoRef.current = valor;

      if (iniciar && valor > 0) {
        fimFaseRef.current =
          Date.now() + valor * 1000;

        setRodando(true);
      } else {
        fimFaseRef.current = null;
        setRodando(false);
      }
    },
    []
  );

  const carregar = useCallback(async () => {
    setLoading(true);

    try {
      const res = await api.get(
        `/aluno/treinos-casa/${treinoCasaId}`
      );

      setTreino(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [treinoCasaId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const iniciarExercicio = useCallback(
    (novoIndice, novaRodada) => {
      const atual = exercicios[novoIndice];

      if (!atual) {
        setFinalizado(true);
        setRodando(false);
        setRestante(0);
        return;
      }

      setIndice(novoIndice);
      setRodada(novaRodada);
      setFase('exercicio');

      definirContagem(
        Number(atual.duracao || 0),
        true
      );
    },
    [definirContagem, exercicios]
  );

  const avancarAposDescanso = useCallback(() => {
    const ultimoExercicio =
      indice >= exercicios.length - 1;

    const ultimaRodada =
      rodada >= totalRodadas;

    if (!ultimoExercicio) {
      iniciarExercicio(
        indice + 1,
        rodada
      );
      return;
    }

    if (!ultimaRodada) {
      iniciarExercicio(
        0,
        rodada + 1
      );
      return;
    }

    setRodando(false);
    setRestante(0);
    setFinalizado(true);
  }, [
    indice,
    rodada,
    exercicios.length,
    totalRodadas,
    iniciarExercicio
  ]);

  const terminarExercicio = useCallback(() => {
    const descanso = Math.max(
      0,
      Number(exercicio?.descanso || 0)
    );

    const ehUltimo =
      indice >= exercicios.length - 1
      && rodada >= totalRodadas;

    if (ehUltimo || descanso <= 0) {
      avancarAposDescanso();
      return;
    }

    setFase('descanso');
    definirContagem(descanso, true);
  }, [
    exercicio,
    indice,
    rodada,
    exercicios.length,
    totalRodadas,
    avancarAposDescanso,
    definirContagem
  ]);

  const finalizarFase = useCallback(() => {
    if (fase === 'descanso') {
      avancarAposDescanso();
    } else {
      terminarExercicio();
    }
  }, [
    fase,
    avancarAposDescanso,
    terminarExercicio
  ]);

  useEffect(() => {
    if (!rodando) {
      return undefined;
    }

    const atualizar = () => {
      const fim = fimFaseRef.current;

      if (!fim) {
        return;
      }

      const novoRestante = Math.max(
        0,
        Math.ceil(
          (fim - Date.now()) / 1000
        )
      );

      setRestante(novoRestante);
      restantePausadoRef.current =
        novoRestante;

      if (novoRestante <= 0) {
        fimFaseRef.current = null;
        setRodando(false);
        finalizarFase();
      }
    };

    atualizar();

    const timer = setInterval(
      atualizar,
      250
    );

    return () => clearInterval(timer);
  }, [rodando, finalizarFase]);

  const iniciarTreino = () => {
    if (!exercicios.length) {
      toast.error(
        'Este treino não possui exercícios'
      );
      return;
    }

    setIniciado(true);
    setFinalizado(false);
    iniciarExercicio(0, 1);
  };

  const pausar = () => {
    if (!rodando) {
      return;
    }

    const fim = fimFaseRef.current;

    const valor = fim
      ? Math.max(
          0,
          Math.ceil(
            (fim - Date.now()) / 1000
          )
        )
      : restante;

    restantePausadoRef.current = valor;
    fimFaseRef.current = null;

    setRestante(valor);
    setRodando(false);
  };

  const continuar = () => {
    const valor = Math.max(
      0,
      Number(
        restantePausadoRef.current
        || restante
        || 0
      )
    );

    if (valor <= 0) {
      finalizarFase();
      return;
    }

    fimFaseRef.current =
      Date.now() + valor * 1000;

    setRodando(true);
  };

  const pular = () => {
    setRodando(false);
    fimFaseRef.current = null;
    setRestante(0);

    finalizarFase();
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-8 w-56 bg-[#141414]" />
        <Skeleton className="h-64 bg-[#141414]" />
        <Skeleton className="h-32 bg-[#141414]" />
      </div>
    );
  }

  if (!treino) {
    return (
      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-8 text-center">
          <p className="text-zinc-400">
            Treino não encontrado.
          </p>

          <Button
            asChild
            variant="outline"
            className="mt-4"
          >
            <Link to="/aluno/treinos-casa">
              Voltar
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (finalizado) {
    return (
      <div className="max-w-xl mx-auto py-6">
        <Card className="bg-[#141414] border-emerald-500/20">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-10 h-10 text-emerald-400" />
            </div>

            <h1 className="text-3xl font-black font-['Barlow_Condensed'] uppercase text-white">
              Treino finalizado!
            </h1>

            <p className="text-zinc-500 mt-2">
              Você completou todas as atividades.
            </p>

            <div className="mt-6 p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/20">
              <Trophy className="w-6 h-6 text-yellow-400 mx-auto mb-2" />

              <p className="text-sm text-zinc-400">
                Recompensa ao concluir
              </p>

              <p className="text-2xl font-black text-yellow-400">
                +{Number(treino.xp_conclusao || 0)} XP
              </p>
            </div>

            <p className="text-xs text-zinc-600 mt-4">
              Nesta etapa de teste o XP ainda não será registrado.
            </p>

            <Button
              asChild
              className="w-full mt-6"
              style={{
                backgroundColor:
                  'var(--cor-primaria, #007AFF)'
              }}
            >
              <Link to="/aluno/treinos-casa">
                Voltar aos treinos
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!iniciado) {
    return (
      <div className="max-w-2xl mx-auto space-y-5">
        <Link
          to="/aluno/treinos-casa"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Link>

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Badge className="mb-3 bg-blue-500/10 text-blue-400 border-blue-500/20">
                  Treino em casa
                </Badge>

                <h1 className="text-3xl font-black font-['Barlow_Condensed'] uppercase text-white">
                  {treino.nome}
                </h1>

                {treino.descricao && (
                  <p className="text-sm text-zinc-400 mt-2">
                    {treino.descricao}
                  </p>
                )}
              </div>

              <div className="text-yellow-400 font-bold flex items-center gap-1">
                <Trophy className="w-4 h-4" />
                {Number(treino.xp_conclusao || 0)} XP
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <div className="bg-[#0F0F0F] rounded-xl p-4 text-center">
                <Dumbbell className="w-5 h-5 text-zinc-500 mx-auto mb-2" />
                <p className="font-bold text-white">
                  {exercicios.length}
                </p>
                <p className="text-xs text-zinc-600">
                  exercícios
                </p>
              </div>

              <div className="bg-[#0F0F0F] rounded-xl p-4 text-center">
                <RotateCcw className="w-5 h-5 text-zinc-500 mx-auto mb-2" />
                <p className="font-bold text-white">
                  {totalRodadas}
                </p>
                <p className="text-xs text-zinc-600">
                  rodadas
                </p>
              </div>
            </div>

            <Button
              onClick={iniciarTreino}
              disabled={!exercicios.length}
              className="w-full h-12 mt-6 font-bold"
              style={{
                backgroundColor:
                  'var(--cor-primaria, #007AFF)'
              }}
            >
              <Play className="w-4 h-4 mr-2" />
              Iniciar treino
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-zinc-600">
            {treino.nome}
          </p>

          <p className="text-sm font-semibold text-white">
            Rodada {rodada} de {totalRodadas}
          </p>
        </div>

        <Badge
          className={
            fase === 'descanso'
              ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
              : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
          }
        >
          {fase === 'descanso'
            ? 'Descanso'
            : `Exercício ${indice + 1}/${exercicios.length}`}
        </Badge>
      </div>

      <Progress
        value={progresso}
        className="h-2 bg-[#27272A]"
      />

      <Card className="bg-[#141414] border-[#27272A] overflow-hidden">
        {fase === 'exercicio' && exercicio?.gif_url ? (
          <div className="bg-black aspect-video flex items-center justify-center overflow-hidden">
            <img
              src={exercicio.gif_url}
              alt={exercicio.nome}
              className="w-full h-full object-contain"
            />
          </div>
        ) : (
          <div className="aspect-video bg-[#0A0A0A] flex items-center justify-center">
            {fase === 'descanso' ? (
              <Clock3 className="w-16 h-16 text-blue-400/50" />
            ) : (
              <Dumbbell className="w-16 h-16 text-zinc-700" />
            )}
          </div>
        )}

        <CardContent className="p-6 text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-600">
            {fase === 'descanso'
              ? 'Prepare-se para continuar'
              : 'Exercício atual'}
          </p>

          <h2 className="text-2xl font-black text-white mt-2">
            {fase === 'descanso'
              ? 'DESCANSO'
              : exercicio?.nome}
          </h2>

          {fase === 'exercicio'
            && exercicio?.instrucoes && (
              <p className="text-sm text-zinc-400 mt-2">
                {exercicio.instrucoes}
              </p>
            )}

          <div
            className={`text-6xl sm:text-7xl font-black tabular-nums mt-6 ${
              fase === 'descanso'
                ? 'text-blue-400'
                : 'text-white'
            }`}
          >
            {formatarTempo(restante)}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-7">
            {rodando ? (
              <Button
                variant="outline"
                onClick={pausar}
                className="h-12 border-[#3f3f46]"
              >
                <Pause className="w-4 h-4 mr-2" />
                Pausar
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={continuar}
                className="h-12 border-[#3f3f46]"
              >
                <Play className="w-4 h-4 mr-2" />
                Continuar
              </Button>
            )}

            <Button
              onClick={pular}
              className="h-12"
              style={{
                backgroundColor:
                  'var(--cor-primaria, #007AFF)'
              }}
            >
              <SkipForward className="w-4 h-4 mr-2" />

              {fase === 'descanso'
                ? 'Pular descanso'
                : 'Próximo'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between text-xs text-zinc-600 px-1">
        <span>
          Etapa {etapaAtual} de {totalEtapas}
        </span>

        <span>
          {progresso}% concluído
        </span>
      </div>
    </div>
  );
}
